import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, debounceTime, delay, map, of, race, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ModalityService } from './modality.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {

  get isLocalOrthancAvailable$(): Observable<PingInterface | undefined> {
    const now = Date.now();
    const fresh = this.orthancAvailable && (now - this.orthancPingTimestamp) < this.PING_CACHE_TTL;
    if (fresh) {
      return of(this.orthancAvailable);
    }
    // Cache the in-flight Observable itself so N concurrent subscribers
    // (e.g. one per study card on a multi-clinic admin worklist) share
    // a SINGLE underlying HTTP probe instead of firing N parallel CORS-blocked requests.
    if (!this.orthancPing$ || (now - this.orthancPingTimestamp) >= this.PING_CACHE_TTL) {
      this.orthancPingTimestamp = now;
      this.orthancPing$ = this.ping().pipe(
        take(1),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.orthancPing$;
  }

  private orthancLocalUrl?: string;
  private get testPath() {
    return '/local/tools/now';
  }

  private orthancAvailable?: PingInterface;
  private orthancPing$?: Observable<PingInterface | undefined>;
  private orthancPingTimestamp = 0;
  private readonly PING_CACHE_TTL = 60_000;

  // Cache pour les vérifications de retry availability avec Subject pour debounce
  private readonly retryAvailabilityCache = new Map<string, { result: Observable<RetryAvailabilityResponse>; timestamp: number }>();
  // 60s de cache local (le cache serveur est aligné à 60s aussi). Réduit le débit
  // de polling vers le worker quand une worklist comporte beaucoup de cartes.
  private readonly RETRY_CACHE_TTL = 60000;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly modalityService: ModalityService
  ) { }

  ping(): Observable<PingInterface | undefined> {
    // LOF Vet (SCC) front: no local client PACS exists — skip the probe entirely
    // (avoids the recurring CORS errors on https://127.0.0.1:<orthancLocalPort>).
    if (environment.mode === 'scc') {
      this.orthancAvailable = { ok: false };
      return of(this.orthancAvailable);
    }

    let $url = of(this.orthancLocalUrl);
    if (!this.orthancLocalUrl || this.orthancLocalUrl === '') {
      $url = this.modalityService.getModalities().pipe(
        map(modalities => modalities.find(modality => modality.modalityTypeName.startsWith('OR'))),
        map(modality => {
          if (!modality) {
            return undefined;
          }

          this.orthancLocalUrl = `${location.protocol}//${modality?.ip}:${environment.orthancLocalPort}`;
          return this.orthancLocalUrl;
        })
      );
    }

    return $url.pipe(
      switchMap(url => {
        if (!url) {
          this.orthancAvailable = { ok: false, url };
          return of(this.orthancAvailable);
        }

        return race(
          of({
            ok: false
          }).pipe(
            delay(500),
          ),
          this.httpClient.get(url + this.testPath, { observe: 'response', responseType: 'text' })
        ).pipe(
          catchError(_err => {
            this.orthancAvailable = { ok: false };
            return of(this.orthancAvailable);
          }),
          map(resp => {
            this.orthancAvailable = {
              ok: resp.ok,
              url: resp.ok ? url : undefined
            };
            return this.orthancAvailable;
          })
        );
      }),
    );
  }

  getPrefetchStatus(): Observable<boolean> {
    let $url = of(this.orthancLocalUrl);
    if (!this.orthancLocalUrl || this.orthancLocalUrl === '') {
      $url = this.modalityService.getModalities().pipe(
        map(modalities => modalities.find(modality => modality.modalityTypeName.startsWith('OR'))),
        map(modality => {
          if (!modality) {
            return undefined;
          }

          this.orthancLocalUrl = `${location.protocol}//${modality?.ip}:${environment.orthancLocalPort}`;
          return this.orthancLocalUrl;
        })
      );
    }

    return $url.pipe(
      switchMap(url => this.httpClient.get<PrefetchStatusInterface>(`${url}/local/mypacs/prefetchEnabled`)),
      tap((answer: PrefetchStatusInterface) => {
        console.log('Prefetch status:', answer.prefetchEnabled);
      }),
      map((answer: PrefetchStatusInterface) => answer.prefetchEnabled)
    );
  }

  sendPrefetchRequest(studyPublicId: string): Observable<any> {
    let $url = of(this.orthancLocalUrl);
    if (!this.orthancLocalUrl || this.orthancLocalUrl === '') {
      $url = this.modalityService.getModalities().pipe(
        map(modalities => modalities.find(modality => modality.modalityTypeName.startsWith('OR'))),
        map(modality => {
          if (!modality) {
            return undefined;
          }

          this.orthancLocalUrl = `${location.protocol}//${modality?.ip}:${environment.orthancLocalPort}`;
          return this.orthancLocalUrl;
        })
      );
    }

    // return this.httpClient.post(`${$url}/prefetch/mypacs/prefetch/${studyPublicId}`, {});

    return $url.pipe(
      switchMap(url => this.httpClient.post(`${url}/prefetch/mypacs/prefetch/${studyPublicId}`, {}))
    );
  }

  /**
   * Checks if the retry transfer button should be displayed for a study
   */
  checkRetryAvailability(studyId: string): Observable<RetryAvailabilityResponse> {
    if (!studyId) {
      return throwError(() => new Error('Study ID is required'));
    }

    // Vérifier le cache local d'abord
    const cached = this.retryAvailabilityCache.get(studyId);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.RETRY_CACHE_TTL) {
      return cached.result;
    }

    // Créer un nouvel Observable partagé et l'ajouter au cache
    const result = this.getWorkerUrl().pipe(
      switchMap(workerUrl => {
        if (!workerUrl) {
          return of({
            shouldShowButton: false,
            message: 'Worker not available',
            activeHangfireJobs: 0,
            activeOrthancJobs: 0,
            cached: false
          });
        }

        return this.httpClient.get<RetryAvailabilityResponse>(`${workerUrl}/api/check/retry/${studyId}`).pipe(
          catchError(error => {
            console.warn('Retry availability check failed:', error);
            // Fallback to conservative approach - don't show button on error
            return of({
              shouldShowButton: false,
              message: 'Availability check failed',
              activeHangfireJobs: 0,
              activeOrthancJobs: 0,
              cached: false
            });
          })
        );
      }),
      shareReplay(1), // Partage le résultat entre plusieurs souscripteurs
      tap(() => {
        // Nettoyer le cache après utilisation pour éviter l'accumulation
        setTimeout(() => {
          this.retryAvailabilityCache.delete(studyId);
        }, this.RETRY_CACHE_TTL);
      })
    );

    // Stocker dans le cache avec timestamp
    this.retryAvailabilityCache.set(studyId, { result, timestamp: now });

    return result;
  }

  /**
   * Retry transfer for an entire study using the Worker API
   */
  retryTransfer(studyId: string): Observable<TransferResponse> {
    if (!studyId) {
      return throwError(() => new Error('Study ID is required'));
    }

    return this.getWorkerUrl().pipe(
      switchMap(workerUrl => {
        if (!workerUrl) {
          return of({ success: false, message: 'Worker not available' });
        }

        return this.httpClient.post<WorkerRetryResponse>(`${workerUrl}/send/retry-transfer/${studyId}`, {}).pipe(
          map(response => ({
            success: response.success,
            message: response.message || (response.success ? 'Transfer initiated successfully' : 'Transfer failed to start')
          })),
          catchError(error => {
            console.error('Transfer retry failed:', error);
            return of({
              success: false,
              message: error.error?.message || 'Transfer failed to start'
            });
          })
        );
      })
    );
  }

  /**
   * Gets the Worker URL based on the local Orthanc modality
   */
  private getWorkerUrl(): Observable<string | undefined> {
    return this.modalityService.getModalities().pipe(
      map(modalities => modalities.find(modality => modality.modalityTypeName.startsWith('OR'))),
      map(modality => {
        if (!modality) {
          return undefined;
        }

        // Worker runs on port 8080, Orthanc on 8042
        return `${location.protocol}//${modality?.ip}/worker`;
      })
    );
  }

}

export interface PingInterface {
  ok: boolean;
  url?: undefined | string  ;
}

export interface PrefetchStatusInterface {
  prefetchEnabled: boolean;
}

export interface TransferResponse {
  success: boolean;
  message?: string;
}

export interface RetryAvailabilityResponse {
  shouldShowButton: boolean;
  message?: string;
  activeHangfireJobs: number;
  activeOrthancJobs: number;
  cached: boolean;
}

export interface WorkerRetryResponse {
  success: boolean;
  message: string;
  studyId: string;
  seriesCount: number;
  enqueuedJobs: number;
  skippedSeries: number;
  worklistJobId: string;
  jobIds: string[];
}
